import React, { useCallback, useEffect, useState } from "react";
import { Col, Form, Modal, Row, Space, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import CustomButton from "components/Common/buttons/CustomButton";
import { createMiniDepot, updateMiniDepot } from "utils/api";
import { defaultNgiErrorMethod, successMessage, warnMessage } from "components/Common/responses/message";
import NGIInput from "components/Common/inputs/NGIInput";
import NGIForm from "components/Common/form/NGIForm";
import NGIFormItem from "components/Common/form/NGIFormItem";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles({
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  modalContent: {
    padding: "30px 0 10px 0",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 24,
  },
  successContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    width: "100%",
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  row: {
    justifyContent: "space-between",
  },
  note: {
    fontSize: 12,
    color: "#444444bf",
    fontStyle: "italic",
  },
});

const AddMiniDepotModal = ({ mainId, mainDepotName, activeItem, onClose, update }) => {
  const classes = useStyles();
  const [form] = Form.useForm();
  const [isLoading, setLoader] = useState(false);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const openConfirmationModal = useCallback(() => setIsConfirmationOpen(true), []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  useEffect(() => {
    if (activeItem) {
      form.setFieldsValue({
        name: activeItem.name,
        address: activeItem.address,
      });
    } else {
      form.setFieldsValue({
        name: mainDepotName,
      });
    }
  }, []);

  const handleSave = useCallback(() => {
    const { name, address } = form.getFieldsValue();
    closeConfirmationModal();
    if (mainDepotName === name) {
      warnMessage("Mini depot name should not be equal to the main depot name");
      return;
    }
    setLoader(true);
    const request = activeItem ? updateMiniDepot : createMiniDepot;
    request(
      {
        ...activeItem,
        name,
        address,
        mainDepotId: mainId,
      },
      activeItem?.id
    )
      .then(() => {
        setTimeout(() => {
          setLoader(false);
          successMessage(`Mini Depot was ${activeItem ? "updated" : "created"} successfully`);
          onClose();
          update();
        }, 500);
      })
      .catch(error => {
        setLoader(false);
        defaultNgiErrorMethod(error);
      });
  }, [mainId, activeItem, form]);

  return (
    <Modal
      open={true}
      title={
        <div className={classes.titleWithDescription}>
          {`${activeItem ? "Edit" : "Add"} Mini Depot`}
          <span>Please fill up all necessary information</span>
          <span className={classes.note}>
            {"Note: Mini depot name should be in <Depot Name>.<Mini Depot Name> format"}
          </span>
        </div>
      }
      onCancel={onClose}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <NGIForm name="addMiniDepot" form={form} onFinish={openConfirmationModal} className={classes.formContainer}>
          <div className={classes.modalContent}>
            <Row className={classes.row}>
              <Col span={11}>
                <Title level={5}>Mini Depot Name</Title>
                <NGIFormItem name="name" rules={[{ required: true, message: "Please enter name" }]}>
                  <NGIInput placeholder="<Depot Name>.<Mini Depot Name>" />
                </NGIFormItem>
              </Col>
              <Col span={11}>
                <Title level={5}>Mini Depot Address</Title>
                <NGIFormItem name="address" rules={[{ required: true, message: "Please enter address" }]}>
                  <NGIInput placeholder="Enter Mini Depot Address" />
                </NGIFormItem>
              </Col>
            </Row>
          </div>
          <Space>
            <CustomButton onClick={onClose} type="primary" color="outlined" size="small" text="Cancel" />
            <CustomButton
              htmlType="submit"
              type="primary"
              size="small"
              text={`${activeItem ? "UPDATE" : "ADD"} MINI DEPOT`}
            />
          </Space>
        </NGIForm>
      </Spin>
      {isConfirmationOpen && (
        <ConfirmationModal
          description={`Are you sure you want to ${activeItem ? "update" : "create"} a mini depot?`}
          onCancel={closeConfirmationModal}
          onConfirm={handleSave}
        />
      )}
    </Modal>
  );
};

export default AddMiniDepotModal;
